import type { Extensions, JSONContent } from "@tiptap/core";
import { generateHTML } from "@tiptap/core";
import { MarkdownManager } from "@tiptap/markdown";
import { StarterKit } from "@tiptap/starter-kit";
import { createContentSchemaExtensions } from "src/tiptap/components/tiptap-node/content-schema-extensions";
import { createImageExtension } from "src/tiptap/components/tiptap-node/image-upload-node/image-upload-kit";
import { normalizeLegacyImageContent } from "src/tiptap/components/tiptap-node/image-upload-node/normalize-legacy-image-content";

/**
 * Render a resolved data-key / handlebars value as HTML through the *editor's own
 * schema* rather than a foreign markdown engine, so a table/image/list in a dynamic
 * node looks identical to the same content in the document.
 *
 * Every data key is authored in a markdown editor, so its resolved value is always
 * markdown. We parse it with the same `MarkdownManager` primitive the backend's
 * `markdownToTiptapJson` uses (`backend/.../tiptap-markdown/content.ts`), then
 * serialize the resulting ProseMirror JSON with Tiptap's `generateHTML`.
 *
 * The extension set is deliberately **schema-only**: it reuses the editor's shared
 * content schema (`createContentSchemaExtensions`, also consumed by `simple-editor.tsx`)
 * plus a base StarterKit, but omits the provider-bound extensions (Collaboration,
 * comments, tracked changes, Selection) — including those in `generateHTML` can throw,
 * and they contribute no schema for answer content.
 */
export const RENDER_EXTENSIONS: Extensions = [
  // StarterKit's built-in horizontalRule is disabled because the shared content schema
  // supplies the custom `HorizontalRule` node (defining it twice would conflict).
  StarterKit.configure({
    horizontalRule: false,
    link: { openOnClick: false },
  }),
  // Base image node — a resolved file answer is `![filename](/share/token)`, which
  // parses to an `image` node and renders as a real `<img>`, matching the document.
  ...createContentSchemaExtensions({
    image: [createImageExtension({ resize: false })],
  }),
];

// The same parser primitive the backend wraps in `markdownToTiptapJson`. Built once.
// TODO: clean editors across frontend and backend
export const markdownManager = new MarkdownManager({
  extensions: RENDER_EXTENSIONS,
});

export const parseMarkdownToTiptapDocument = (markdown: string): JSONContent =>
  markdownManager.parse(markdown) as JSONContent;

export interface RenderedMarkdown {
  /** HTML rendered through the editor schema for `dangerouslySetInnerHTML`. */
  html: string;
  /**
   * True when the value is genuinely block-level (a table, image, list, heading, or
   * more than one block) rather than a single paragraph of inline content. Inline
   * values flow within the surrounding sentence; block values sit on their own line.
   */
  isBlock: boolean;
}

export interface ParsedMarkdownContent {
  content: JSONContent[];
  inlineContent: JSONContent[];
  isBlock: boolean;
}

const cache = new Map<string, RenderedMarkdown>();

const STRIP_OPEN_PARAGRAPH = /^<p[^>]*>/;
const STRIP_CLOSE_PARAGRAPH = /<\/p>\s*$/;

const getDocumentContent = (doc: JSONContent): JSONContent[] =>
  Array.isArray(doc.content) ? doc.content : [];

const hasSingleParagraph = (content: JSONContent[]): boolean =>
  content.length === 1 && content[0]?.type === "paragraph";

const getParsedMarkdownContent = (
  markdown: string
): ParsedMarkdownContent & { doc: JSONContent } => {
  const doc = normalizeLegacyImageContent(markdownManager.parse(markdown));
  const content = getDocumentContent(doc);
  const isBlock = !hasSingleParagraph(content);

  return {
    doc,
    content,
    inlineContent: isBlock ? [] : (content[0]?.content ?? []),
    isBlock,
  };
};

const getFallbackTiptapContent = (markdown: string): ParsedMarkdownContent => {
  const inlineContent: JSONContent[] = markdown
    ? [{ type: "text", text: markdown }]
    : [];

  return {
    isBlock: false,
    content: inlineContent.length
      ? [{ type: "paragraph", content: inlineContent }]
      : [],
    inlineContent,
  };
};

const unwrapParagraphHtml = (html: string): string =>
  html.replace(STRIP_OPEN_PARAGRAPH, "").replace(STRIP_CLOSE_PARAGRAPH, "");

/**
 * Render a markdown string to schema HTML. A lone paragraph is unwrapped to its
 * inline HTML so plain text / bold / links flow inline; anything block-level keeps
 * its block markup. Falls back to the raw (text-escaped) string if parsing fails.
 */
export const renderMarkdownToHtml = (markdown: string): RenderedMarkdown => {
  const cached = cache.get(markdown);
  if (cached) {
    return cached;
  }

  let result: RenderedMarkdown;
  try {
    const { doc, isBlock } = getParsedMarkdownContent(markdown);
    const html = generateHTML(doc, RENDER_EXTENSIONS);
    result = {
      html: isBlock ? html : unwrapParagraphHtml(html),
      isBlock,
    };
  } catch {
    result = { html: escapeHtml(markdown), isBlock: false };
  }

  cache.set(markdown, result);
  return result;
};

// Version comparison needs parsed Tiptap JSON, not HTML, so resolved dynamic
// values can be compared as ordinary inline or block document content.
export const parseMarkdownToTiptapContent = (
  markdown: string
): ParsedMarkdownContent => {
  try {
    const parsed = getParsedMarkdownContent(markdown);
    return {
      content: parsed.content,
      inlineContent: parsed.inlineContent,
      isBlock: parsed.isBlock,
    };
  } catch {
    return getFallbackTiptapContent(markdown);
  }
};

const escapeHtml = (value: string): string =>
  value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
